import { COMPETENCE_ICONS } from '../constants/competenceIcons';
import { displayCompetenceName } from '../constants/cartographieColors';
import { isSoftSkillsSeries } from '../constants/badgeAxes';

// Local mapping of badge names and levels to existing static assets.
// This preserves previous visuals while backend does not yet provide image URLs.
import { getCpsLocalBadgeImage, isCompetencesPsychosocialesSeries } from './cpsSeries';

const badgeImagesByName: Record<string, string> = {
  // Série TouKouLeur (universelle)
  "Adaptabilité": "/NIV 1/Adaptabilite@2x.png",
  "Communication": "/NIV 1/Communication@2x.png",
  "Coopération": "/NIV 1/Cooperation@2x.png",
  "Créativité": "/NIV 1/Creativite@2x.png",
  "Engagement": "/NIV 1/Engagement@2x.png",
  "Esprit Critique": "/NIV 1/EspritCritique@2x.png",
  "Formation": "/NIV 1/Formation@2x.png",
  "Gestion de Projet": "/NIV 1/GestionDePvrojet@2x.png",
  "Information Numérique": "/NIV 1/Inform Numerique@2x.png",
  "Organisation Opérationnelle": "/NIV 1/OrganisationOpe@2x.png",
  "Sociabilité": "/NIV 1/Sociabilite@2x.png",

  // Série Audiovisuelle - Level 1
  "ORGANISATION-LOGISTIQUE": "/badges_audiovisuels/Badge_Organisation_Logistique_Level1.png",
  "IMAGE": "/badges_audiovisuels/Badge_Image_Level1.png",
  "SON": "/badges_audiovisuels/Badge_Son_Level1.png",
  "ORGANISATION-ARTISTIQUE": "/badges_audiovisuels/Badge_Organisation_Artistique_Level1.png",
  
  // Série Audiovisuelle - Level 2 (ACTING also exists at level 2, so using level 2 image)
  "ACTING": "/badges_audiovisuels/Badge_Acting_Level2.png",
  "PRODUCTION": "/badges_audiovisuels/Badge_Production_Level2.png",
  "REGIE": "/badges_audiovisuels/Badge_Regie_Level2.png",
  "MISE EN SCENE": "/badges_audiovisuels/Badge_Mise_en_Scene_Level2.png",
  "PRISE IMAGE & LUMIERE": "/badges_audiovisuels/Badge_Prise_Image_Level2.png",
  "POSTPRODUCTION IMAGE ET VFX": "/badges_audiovisuels/Badge_Postproduction_Image_VFX_Level2.png",
  "PRISE DE SON": "/badges_audiovisuels/Badge_Prise_Son_Level2.png",
  "POST PRODUCTION SON": "/badges_audiovisuels/Badge_PostProduction_Son_Level2.png",
  "DECO & SFX": "/badges_audiovisuels/Badge_Deco_SFX_Level2.png",
  "STYLISME& HMC": "/badges_audiovisuels/Badge_Stylisme_HMC_Level2.png",

  // Série Parcours des possibles
  "Étape 1 : IMPLICATION INITIALE": "/badges_parcours_possibles/Badge-Serie-des-possibles-Level1.png",
  "Étape 2: ENGAGEMENT ENCADRÉ": "/badges_parcours_possibles/Badge-Serie-des-possibles-Level2.png",
  "Étape3": "/badges_parcours_possibles/Badge-Serie-des-possibles-Level3.png",
  "Étape4": "/badges_parcours_possibles/Badge-Serie-des-possibles-Level4.png",

  // Série Parcours professionnel
  "PARCOURS DE DÉCOUVERTE - COLLÈGE": "/badges_parcours_professionnel/Badge_Parcours_Professionnel_Level1.png",
  "PARCOURS DE FORMATION - LYCÉE": "/badges_parcours_professionnel/Badge_Parcours_Professionnel_Level2.png",
  "PARCOURS DE PROFESSIONNALISATION - POST-BAC": "/badges_parcours_professionnel/Badge_Parcours_Professionnel_Level3.png",
  "EXPÉRIENCES PROFESSIONNELLES": "/badges_parcours_professionnel/Badge_Parcours_Professionnel_Level4.png",
};

// Level-specific mappings for badges that have the same name at different levels
// Structure: series -> badge name -> level -> image path
const badgeImagesByLevel: Record<string, Record<string, Record<string, string>>> = {
  "Série Audiovisuelle": {
    "ACTING": {
      "level_1": "/badges_audiovisuels/Badge_Acting_Level1.png",
      "level_2": "/badges_audiovisuels/Badge_Acting_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Acting_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Acting_Level4.png",
    },
    "PRODUCTION": {
      "level_2": "/badges_audiovisuels/Badge_Production_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Production_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Production_Level4.png",
    },
    "REGIE": {
      "level_2": "/badges_audiovisuels/Badge_Regie_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Regie_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Regie_Level4.png",
    },
    "MISE EN SCENE": {
      "level_2": "/badges_audiovisuels/Badge_Mise_en_Scene_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Mise_en_Scene_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Mise_en_Scene_Level4.png",
    },
    "PRISE IMAGE & LUMIERE": {
      "level_2": "/badges_audiovisuels/Badge_Prise_Image_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Prise_Image_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Prise_Image_Level4.png",
    },
    "POSTPRODUCTION IMAGE ET VFX": {
      "level_2": "/badges_audiovisuels/Badge_Postproduction_Image_VFX_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Postproduction_Image_VFX_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Postproduction_Image_VFX_Level4.png",
    },
    "PRISE DE SON": {
      "level_2": "/badges_audiovisuels/Badge_Prise_Son_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Prise_Son_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Prise_Son_Level4.png",
    },
    "POST PRODUCTION SON": {
      "level_2": "/badges_audiovisuels/Badge_PostProduction_Son_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_PostProduction_Son_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_PostProduction_Son_Level4.png",
    },
    "DECO & SFX": {
      "level_2": "/badges_audiovisuels/Badge_Deco_SFX_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Deco_SFX_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Deco_SFX_Level4.png",
    },
    "STYLISME& HMC": {
      "level_2": "/badges_audiovisuels/Badge_Stylisme_HMC_Level2.png",
      "level_3": "/badges_audiovisuels/Badge_Stylisme_HMC_Level3.png",
      "level_4": "/badges_audiovisuels/Badge_Stylisme_HMC_Level4.png",
    },
  },
  "Série TouKouLeur": {
    "Adaptabilité": { "level_2": "/badges_toutkouleur_niveau2/Adaptabilite.png", "level_3": "/badges_toukouleur_niveau3/Adaptabilite-.jpg", "level_4": "/badges_toukouleur_niveau4/Adaptabilite.jpg" },
    "Communication": { "level_2": "/badges_toutkouleur_niveau2/Communication.png", "level_3": "/badges_toukouleur_niveau3/Communication.jpg", "level_4": "/badges_toukouleur_niveau4/Communication.jpg" },
    "Coopération": { "level_2": "/badges_toutkouleur_niveau2/Cooperation.png", "level_3": "/badges_toukouleur_niveau3/Cooperation.jpg", "level_4": "/badges_toukouleur_niveau4/Cooperation.jpg" },
    "Créativité": { "level_2": "/badges_toutkouleur_niveau2/Creativite8.png", "level_3": "/badges_toukouleur_niveau3/Creativite.png", "level_4": "/badges_toukouleur_niveau4/Creativite.jpg" },
    "Engagement": { "level_2": "/badges_toutkouleur_niveau2/Engagement.png", "level_3": "/badges_toukouleur_niveau3/Engagement.jpg", "level_4": "/badges_toukouleur_niveau4/Engagement.jpg" },
    "Esprit Critique": { "level_2": "/badges_toutkouleur_niveau2/EspritCritique8.png", "level_3": "/badges_toukouleur_niveau3/EspritCritique.jpg", "level_4": "/badges_toukouleur_niveau4/EspritCritique.jpg" },
    "Formation": { "level_2": "/badges_toutkouleur_niveau2/Formation8.png", "level_3": "/badges_toukouleur_niveau3/Formation.jpg", "level_4": "/badges_toukouleur_niveau4/Formation.jpg" },
    "Gestion de Projet": { "level_2": "/badges_toutkouleur_niveau2/GestionDePvrojet.png", "level_3": "/badges_toukouleur_niveau3/GestionDePvrojet.jpg", "level_4": "/badges_toukouleur_niveau4/GestionDePvrojet.jpg" },
    "Information Numérique": { "level_2": "/badges_toutkouleur_niveau2/Informatique Numerique.png", "level_3": "/badges_toukouleur_niveau3/Informatique Numerique.jpg", "level_4": "/badges_toukouleur_niveau4/Informatique Numerique.jpg" },
    "Organisation Opérationnelle": { "level_2": "/badges_toutkouleur_niveau2/OrganisationOperationnelle.png", "level_3": "/badges_toukouleur_niveau3/OrganisationOperationnelle.jpg", "level_4": "/badges_toukouleur_niveau4/OrganisationOperationnellejpg.jpg" },
    "Sociabilité": { "level_2": "/badges_toutkouleur_niveau2/Sociabilite.png", "level_3": "/badges_toukouleur_niveau3/Sociabilite.jpg", "level_4": "/badges_toukouleur_niveau4/Sociabilite.jpg" },
  },
  "Série Compétences à s'orienter - Collège": {
    "Compétence 1 – Chercher et trier l'information": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/chercher_et_trier_linformation.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/chercher_et_trier_linformation.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/chercher_et_trier_linformation.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/chercher_et_trier_linformation.png",
    },
    "Compétence 2 – Connaitre les personnes, lieux, ressources qui peuvent m'aider": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/connaitre_personnes_qui_peuvent_aider.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/connaitre_personnes_qui_peuvent_aider.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/connaitre_personnes_qui_peuvent_aider.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/connaitre_personnes_qui_peuvent_aider.png",
    },
    "Compétence 3 – Apprendre à découvrir les parcours de formation": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/apprendre_a_decovrir_les_parcours.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/apprendre_a_decovrir_les_parcours.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/apprendre_a_decovrir_les_parcours.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/apprendre_a_decovrir_les_parcours.png",
    },
    "Compétence 4 – Apprendre à découvrir les métiers et le monde du travail": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/apprendre_a_decouvrir_les_metiers_et_le_monde_du_travail.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/apprendre_a_decouvrir_les_metiers_et_le_monde_du_travail.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/apprendre_a_decouvrir_les_metiers_et_le_monde_du_travail.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/apprendre_a_decouvrir_les_metiers_et_le_monde_du_travail.png",
    },
    "Compétence 5 – M'interroger sur les clichés": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/minterroger_sur_les_cliches.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/minterroger_sur_les_cliches.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/minterroger_sur_les_cliches.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/minterroger_sur_les_cliches.png",
    },
    "Compétence 1 – Apprendre à me connaitre": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/apprendre_a_me_connaitre.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/apprendre_a_me_connaitre.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/apprendre_a_me_connaitre.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/apprendre_a_me_connaitre.png",
    },
    "Compétence 2 – Définir mes projets en fonction de qui je suis": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/définir_mes_projets_en_fonction_de_qui_je_suis.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/définir_mes_projets_en_fonction_de_qui_je_suis.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/définir_mes_projets_en_fonction_de_qui_je_suis.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/définir_mes_projets_en_fonction_de_qui_je_suis.png",
    },
    "Compétence 3 – M'autoriser à rêver et à avoir des ambitions": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/mautoriser_rever_et_a_avoir_des_ambitions.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/mautoriser_rever_et_a_avoir_des_ambitions.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/mautoriser_rever_et_a_avoir_des_ambitions.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/mautoriser_rever_et_a_avoir_des_ambitions.png",
    },
    "Compétence 4 – Savoir me présenter et m'affirmer": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/savoir_me_présenter_et_maffirmer.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/savoir_me_présenter_et_maffirmer.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/savoir_me_présenter_et_maffirmer.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/savoir_me_présenter_et_maffirmer.png",
    },
    "Compétence 5 – Identifier ce que j'ai appris et ce que je sais faire": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/identifier_ce_que_jai_appris_et_ce_que_je_sais_faire.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/identifier_ce_que_jai_appris_et_ce_que_je_sais_faire.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/identifier_ce_que_jai_appris_et_ce_que_je_sais_faire.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/identifier_ce_que_jai_appris_et_ce_que_je_sais_faire.png",
    },
    "Compétence 1 – Accepter les imprévus et saisir les occasions": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/accepter_les_imprevus_et_saisir_les_occasions.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/accepter_les_imprevus_et_saisir_les_occasions.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/accepter_les_imprevus_et_saisir_les_occasions.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/accepter_les_imprevus_et_saisir_les_occasions.png",
    },
    "Compétence 2 – M'ouvrir au monde et aux autres": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/mouvrir_au_monde_et_aux_autres.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/mouvrir_au_monde_et_aux_autres.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/mouvrir_au_monde_et_aux_autres.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/mouvrir_au_monde_et_aux_autres.png",
    },
    "Compétence 3 – Me préparer aux transitions et aux changements": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/me_preparer_aux_transitions_et_aux_changements.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/me_preparer_aux_transitions_et_aux_changements.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/me_preparer_aux_transitions_et_aux_changements.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/me_preparer_aux_transitions_et_aux_changements.png",
    },
    "Compétence 4 – Me projeter et comprendre les conséquences de mes choix": {
      level_1: "/badges_competences_a_sorienter_au_college/niveau_1/me_projeter_et_comprendre_les_consequences_de_mes_choix.png",
      level_2: "/badges_competences_a_sorienter_au_college/niveau_2/me_projeter_et_comprendre_les_consequences_de_mes_choix.png",
      level_3: "/badges_competences_a_sorienter_au_college/niveau_3/me_projeter_et_comprendre_les_consequences_de_mes_choix.png",
      level_4: "/badges_competences_a_sorienter_au_college/niveau_4/me_projeter_et_comprendre_les_consequences_de_mes_choix.png",
    },
  },
  "Série Métiers de la mer": {
    "Niveau 1 - Se situer et s'adapter dans un collectif": {
      level_1: "/badges_metiers_de_la_mer/axe_1_vie_en_collectivite/niveau_1_sesituer_et_sadapter.png",
    },
    "Niveau 1 - Respect des autres": {
      level_1: "/badges_metiers_de_la_mer/axe_1_vie_en_collectivite/niveau_1_respect_des_autres.png",
    },
    "Niveau 1 - Respect du vivant et de l'environnement": {
      level_1: "/badges_metiers_de_la_mer/axe_1_vie_en_collectivite/niveau_1_respect_du_vivant.png",
    },
    "Niveau 2 - Contribuer activement au collectif": {
      level_2: "/badges_metiers_de_la_mer/axe_1_vie_en_collectivite/niveau_2_contribuer_activement_au_collectif.png",
    },
    "Niveau 1 - Comprendre et appliquer les règles de sécurité maritime": {
      level_1: "/badges_metiers_de_la_mer/axe_2_experience_de_la_mer_et_securite/niveau_1_comprendre_et_appliquer_les_regles_de_securite_maritime.png",
    },
    "Niveau 1 - Responsabilité": {
      level_1: "/badges_metiers_de_la_mer/axe_2_experience_de_la_mer_et_securite/niveau_1_responsabilite.png",
    },
    "Niveau 2 - Agir avec autonomie et sens marin": {
      level_2: "/badges_metiers_de_la_mer/axe_2_experience_de_la_mer_et_securite/niveau_2_agir_avec_autonomie_et_sens_marin.png",
    },
    "Niveau 1 - S'impliquer dans un parcours de découverte": {
      level_1: "/badges_metiers_de_la_mer/axe_3_engagement_et_construction_du_projet_professionnel/niveau_1_simpliquer_dans_un_parcours_de_decouverte.png",
    },
    "Niveau 2 - Se projeter et construire des perspectives": {
      level_2: "/badges_metiers_de_la_mer/axe_3_engagement_et_construction_du_projet_professionnel/niveau_2_seprojeter_et_construire_des_perspectives.png",
    },
  },
};

/**
 * Icône d'une compétence, partout où elle est demandée dans l'appli.
 * Priorité à l'icône spécifique de la maquette KIN_UX_CARTOGRAPHIE_V1_1
 * (competenceIcons.ts, encodée en data URI SVG) ; repli sur les anciens visuels
 * PNG/JPG ci-dessus si aucune icône n'existe pour ce nom.
 * @param allowVectorIcon - à false pour forcer un visuel raster (ex. export PDF, qui ne
 * sait pas afficher du SVG) même quand une icône vectorielle existe.
 */
export const getLocalBadgeImage = (
  badgeName?: string,
  badgeLevel?: string,
  badgeSeries?: string,
  opts?: { allowVectorIcon?: boolean }
): string | undefined => {
  if (!badgeName) return undefined;

  // Compétences psychosociales: pictogramme de la compétence générale (C1…S2)
  if (isCompetencesPsychosocialesSeries(badgeSeries)) {
    return getCpsLocalBadgeImage(badgeName);
  }

  if (opts?.allowVectorIcon ?? true) {
    const svg = COMPETENCE_ICONS[displayCompetenceName(badgeName)];
    if (svg) {
      const fullSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">${svg}</svg>`;
      return `data:image/svg+xml;utf8,${encodeURIComponent(fullSvg)}`;
    }
  }

  // Check level-specific mapping first (for badges with same name at different levels)
  const seriesKey = isSoftSkillsSeries(badgeSeries) ? 'Série TouKouLeur' : badgeSeries;
  if (badgeLevel && seriesKey && badgeImagesByLevel[seriesKey]?.[badgeName]?.[badgeLevel]) {
    return badgeImagesByLevel[seriesKey][badgeName][badgeLevel] as string;
  }

  // Fallback to name-only mapping
  return badgeImagesByName[badgeName];
};

